<script setup lang="ts">
import { computed, inject } from 'vue';
import SidePeekIcon from '@/components/icons/SidePeekIcon.vue';
import { SIDE_PEEK_BUTTON_FLOATS } from '@/composables/sidePeekButtonPlacement';
import { useOpenRecordInInspector } from '@/composables/useOpenRecordInInspector';

/**
 * The small button beside a record link that opens the record in a peek
 * instead of on its own page, so you choose each time. Its icon and its words
 * name the peek you will get, side or center. It shows when the
 * enclosing `group/recordlink` is hovered or focused. A touch screen has no
 * hover, so there it always shows. A phone has no side panel, so it is hidden
 * there.
 *
 * A plain `title` rather than a Tooltip: one sits in every link cell of a
 * table, and mounting a tooltip per cell froze the page on load once before.
 */
const props = withDefaults(
    defineProps<{
        entityId: string;
        recordId: number | string;
        label: string;
        /**
         * Take no room until the name is hovered or focused, for a name inside
         * running text, where a reserved gap would show between words.
         */
        revealOnHover?: boolean;
    }>(),
    { revealOnHover: false },
);

const { openRecord, opensInPane } = useOpenRecordInInspector();
// In a right-aligned value cell, float left of the value rather than take room after it.
const floats = inject(SIDE_PEEK_BUTTON_FLOATS, false);

/** On a list the record shows in the list's pane, so the button says so. */
const actionLabel = computed(() => (opensInPane() ? 'Show in the pane' : 'Open in side peek'));

function openInPeek(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    openRecord(props.entityId, props.recordId);
}
</script>

<template>
  <button
    type="button"
    :title="actionLabel"
    :aria-label="opensInPane() ? `Show ${label} in the pane` : `Open ${label} in side peek`"
    data-testid="open-in-side-peek"
    class="hidden shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none"
    :class="[
        revealOnHover
            ? 'md:group-focus-within/recordlink:inline-flex md:group-hover/recordlink:inline-flex md:[@media(hover:none)]:inline-flex'
            : 'opacity-0 transition-opacity group-hover/recordlink:opacity-100 focus-visible:opacity-100 md:inline-flex [@media(hover:none)]:opacity-100',
        floats && 'absolute top-1/2 right-full mr-1 -translate-y-1/2',
    ]"
    @click="openInPeek"
  >
    <SidePeekIcon class="size-3.5" />
  </button>
</template>
